import { useState, useEffect, Fragment } from 'react'
import { Download, ChevronRight, ChevronDown } from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { SERVICE_AREAS, isActiveProfile } from './constants'

const LEVEL_LABEL = { ad: 'AD', am: 'AM', technician: 'Technician', supervising_ad: 'Supervising AD' }
const serviceAreaLabel = v => SERVICE_AREAS.find(a => a.value === v)?.label || v || ''
const hoursDec = seconds => ((seconds || 0) / 3600).toFixed(3)
const fmtHrs = n => (n || 0).toFixed(3)
const CATEGORY_LABEL = { task: 'Task', meeting: 'Meeting', admin: 'Admin' }
const fmtLineDate = d => { try { return format(new Date(d + 'T12:00:00'), 'EEE, MMM d') } catch { return d } }

const toDateStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// Date-range presets computed with plain Date math (no pay-period cadence assumed yet).
function presetRange(which) {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  if (which === 'this_month') return [toDateStr(new Date(y, m, 1)), toDateStr(now)]
  if (which === 'last_month') return [toDateStr(new Date(y, m - 1, 1)), toDateStr(new Date(y, m, 0))]
  if (which === 'this_week') {
    const start = new Date(now)
    start.setDate(now.getDate() - now.getDay()) // Sunday start
    return [toDateStr(start), toDateStr(now)]
  }
  return [toDateStr(new Date(y, m, 1)), toDateStr(now)]
}

const csvEsc = v => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export default function TimeReportView({ profiles, allClients, profile }) {
  const toast = useToast()
  const [from, setFrom] = useState(() => presetRange('this_month')[0])
  const [to, setTo] = useState(() => presetRange('this_month')[1])
  const [personId, setPersonId] = useState('')
  const [clientId, setClientId] = useState('')
  const [level, setLevel] = useState('')
  const [groupBy, setGroupBy] = useState('person')
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    let q = supabase.from('time_entries').select('*')
      .gte('entry_date', from).lte('entry_date', to)
      .order('entry_date', { ascending: false })
    if (personId) q = q.eq('user_id', personId)
    if (clientId) q = q.eq('client_id', clientId)
    if (level) q = q.eq('pay_level', level)
    q.limit(5000).then(({ data, error }) => {
      if (cancelled) return
      setLoading(false)
      if (error) { toast('Error loading report: ' + error.message, 'error'); return }
      setEntries(data || [])
    })
    return () => { cancelled = true }
  }, [from, to, personId, clientId, level]) // toast/profiles stable; refetch on filter change

  // Restricted to supervising AD (payroll + pay-level data). Placed after all
  // hooks so hook order is identical on every render.
  if (profile?.role !== 'supervising_ad') {
    return <div className="empty-state">This view is restricted.</div>
  }

  const personName = id => profiles.find(p => p.id === id)?.full_name || 'Unknown'
  const clientName = id => allClients.find(c => c.id === id)?.name || 'Unknown client'

  const applyPreset = which => {
    const [f, t] = presetRange(which)
    setFrom(f); setTo(t)
  }

  // ── Aggregate into the selected grouping, split by pay level ─────────────────
  const groupKey = e => {
    if (groupBy === 'client') return e.client_id || '__none__'
    if (groupBy === 'day') return e.entry_date
    return e.user_id
  }
  const groupLabel = (key) => {
    if (groupBy === 'client') return key === '__none__' ? 'No client' : clientName(key)
    if (groupBy === 'day') return key
    return personName(key)
  }

  // Round PER ENTRY first (to the same precision shown on each line), then sum
  // those rounded values for the group/grand totals. This guarantees the total
  // always equals exactly what you get from adding up the visible line items —
  // summing raw seconds and rounding once at the end looks "more precise" but
  // produces a total that can never be reconciled by hand against the rows above it.
  const hoursNum = seconds => Math.round(((seconds || 0) / 3600) * 1000) / 1000

  const map = {}
  let grand = { ad: 0, am: 0, technician: 0, other: 0, total: 0 }
  entries.forEach(e => {
    const k = groupKey(e)
    if (!map[k]) map[k] = { ad: 0, am: 0, technician: 0, other: 0, total: 0, items: [] }
    const hrs = hoursNum(e.duration_seconds)
    const bucket = ['ad', 'am', 'technician'].includes(e.pay_level) ? e.pay_level : 'other'
    map[k][bucket] += hrs
    map[k].total += hrs
    map[k].items.push(e)
    grand[bucket] += hrs
    grand.total += hrs
  })
  const rows = Object.keys(map)
    .map(k => ({ key: k, label: groupLabel(k), ...map[k] }))
    .sort((a, b) => b.total - a.total)

  const toggleRow = key => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))

  const exportCsv = () => {
    if (!entries.length) { toast('Nothing to export for this range', 'error'); return }
    const header = ['Date', 'Person', 'Client', 'Service Area', 'Type', 'Pay Level', 'Hours', 'Notes']
    const lines = [header]
    entries.forEach(e => lines.push([
      e.entry_date,
      personName(e.user_id),
      e.client_id ? clientName(e.client_id) : 'No client',
      serviceAreaLabel(e.service_area),
      e.category,
      LEVEL_LABEL[e.pay_level] || e.pay_level,
      hoursDec(e.duration_seconds),
      e.description || '',
    ]))
    const csv = lines.map(r => r.map(csvEsc).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `time-report-${from}_to_${to}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const th = { textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }
  const thNum = { ...th, textAlign: 'right' }
  const td = { padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-light)' }
  const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">From</label>
            <input className="form-control" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">To</label>
            <input className="form-control" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Person</label>
            <select className="form-control" value={personId} onChange={e => setPersonId(e.target.value)}>
              <option value="">Everyone</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.full_name}{isActiveProfile(p) ? '' : ' (deactivated)'}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Client</label>
            <select className="form-control" value={clientId} onChange={e => setClientId(e.target.value)}>
              <option value="">All clients</option>
              {[...allClients].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Pay level</label>
            <select className="form-control" value={level} onChange={e => setLevel(e.target.value)}>
              <option value="">All levels</option>
              <option value="ad">AD</option>
              <option value="am">AM</option>
              <option value="technician">Technician</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Group by</label>
            <select className="form-control" value={groupBy} onChange={e => { setGroupBy(e.target.value); setExpanded({}) }}>
              <option value="person">Person</option>
              <option value="client">Client</option>
              <option value="day">Day</option>
            </select>
          </div>
        </div>
        <div className="card-body" style={{ paddingTop: 0, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Quick range:</span>
          <button className="btn btn-sm btn-ghost" onClick={() => applyPreset('this_week')}>This week</button>
          <button className="btn btn-sm btn-ghost" onClick={() => applyPreset('this_month')}>This month</button>
          <button className="btn btn-sm btn-ghost" onClick={() => applyPreset('last_month')}>Last month</button>
          <button className="btn btn-sm btn-gold" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={exportCsv}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14, color: 'var(--text-secondary)', fontSize: 14 }}>
        <span><strong style={{ color: 'var(--text-primary)' }}>{fmtHrs(grand.total)}</strong> total hrs</span>
        <span>AD <strong>{fmtHrs(grand.ad)}</strong></span>
        <span>AM <strong>{fmtHrs(grand.am)}</strong></span>
        <span>Tech <strong>{fmtHrs(grand.technician)}</strong></span>
        <span style={{ color: 'var(--text-muted)' }}>{entries.length} entries</span>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: 24 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">No time logged in this range.</div>
      ) : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>{groupBy === 'person' ? 'Person' : groupBy === 'client' ? 'Client' : 'Day'}</th>
                <th style={thNum}>AD</th>
                <th style={thNum}>AM</th>
                <th style={thNum}>Tech</th>
                <th style={thNum}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isOpen = !!expanded[r.key]
                const items = [...r.items].sort((a, b) =>
                  (b.entry_date || '').localeCompare(a.entry_date || '') ||
                  String(b.created_at || '').localeCompare(String(a.created_at || ''))
                )
                return (
                  <Fragment key={r.key}>
                    <tr
                      onClick={() => toggleRow(r.key)}
                      style={{ cursor: 'pointer' }}
                      title={isOpen ? 'Hide line items' : 'Show line items'}
                    >
                      <td style={td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          {r.label}
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                            ({r.items.length})
                          </span>
                        </span>
                      </td>
                      <td style={tdNum}>{r.ad ? fmtHrs(r.ad) : '—'}</td>
                      <td style={tdNum}>{r.am ? fmtHrs(r.am) : '—'}</td>
                      <td style={tdNum}>{r.technician ? fmtHrs(r.technician) : '—'}</td>
                      <td style={{ ...tdNum, fontWeight: 700 }}>{fmtHrs(r.total)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0, background: 'var(--cream)' }}>
                          {items.map(e => {
                            const secondary = [
                              groupBy !== 'person' ? personName(e.user_id) : null,
                              groupBy === 'person' || groupBy === 'day'
                                ? (e.client_id ? clientName(e.client_id) : 'No client')
                                : null,
                              e.service_area ? serviceAreaLabel(e.service_area) : null,
                              CATEGORY_LABEL[e.category] || e.category,
                              LEVEL_LABEL[e.pay_level] || e.pay_level,
                            ].filter(Boolean).join(' · ')
                            return (
                              <div
                                key={e.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 12,
                                  padding: '8px 12px 8px 32px',
                                  borderBottom: '1px solid var(--border-light)',
                                }}
                              >
                                <div style={{ minWidth: 96, fontSize: 12, color: 'var(--text-secondary)', paddingTop: 1 }}>
                                  {fmtLineDate(e.entry_date)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{secondary}</div>
                                  {e.description && (
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                                      {e.description}
                                    </div>
                                  )}
                                </div>
                                <div style={{ ...tdNum, padding: 0, minWidth: 56, fontSize: 13, fontWeight: 600 }}>
                                  {hoursDec(e.duration_seconds)}
                                </div>
                              </div>
                            )
                          })}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...td, fontWeight: 700, borderTop: '2px solid var(--border)' }}>Total</td>
                <td style={{ ...tdNum, fontWeight: 700, borderTop: '2px solid var(--border)' }}>{fmtHrs(grand.ad)}</td>
                <td style={{ ...tdNum, fontWeight: 700, borderTop: '2px solid var(--border)' }}>{fmtHrs(grand.am)}</td>
                <td style={{ ...tdNum, fontWeight: 700, borderTop: '2px solid var(--border)' }}>{fmtHrs(grand.technician)}</td>
                <td style={{ ...tdNum, fontWeight: 700, borderTop: '2px solid var(--border)' }}>{fmtHrs(grand.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {grand.other > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          Note: {fmtHrs(grand.other)} hrs logged at another level (e.g. supervising AD) are included in totals but not the AD/AM/Tech columns.
        </div>
      )}
    </div>
  )
}